import { useEffect, useMemo, useRef, useState } from 'react'

import { useCreateIncidentRules } from '../hooks/useCreateIncidentRules'
import { useMenuPlacement } from '../hooks/useMenuPlacement'
import { SubmitHandler, useForm } from 'react-hook-form'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import { Box, Flex, ScaleFade } from '@chakra-ui/react'

import {
  Button,
  FormDatePickerControl,
  FormInputSelectControl,
  FormRadioInputControl,
  FormTextareaControl,
} from '@/components/ui'
import {
  Action,
  RepeatInterval,
  RuleEventType,
} from '@/graphql/generated/schemas'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'
import { uuid } from '@/utils/helpers'

import { AiOperatorCriteria } from '../ai-operator-criteria/components/AiOperatorCriteria'
import { DryRunToggle } from '../ai-operator-criteria/components/DryRunToggle'
import { Criterion } from '../ai-operator-criteria/types/types'
import { FormInputsT } from '../types/types'
import {
  MAX_RULENAME_LENGTH,
  actionOptions,
  areValidEventsForDoorDevice,
  getOptionValues,
  isEmailActionValid,
} from '../utils/utils'
import { CrossDayAlert } from './CrossDayAlert'
import { EmailInputSelectControl } from './EmailInputSelectControl'
import { EventSectionAlert } from './EventSectionAlert'
import { LabeledSwitch } from './LabeledSwitch'
import { RecurrenceToggle } from './RecurrenceToggle'
import { RuleScheduleAlert } from './RuleScheduleAlert'
import { RuleSection } from './RuleSection'

export const CreateIncidentRules = () => {
  const scrollViewRef = useRef<HTMLDivElement>(null)
  const doorsRef = useRef<HTMLDivElement>(null)
  const camerasRef = useRef<HTMLDivElement>(null)
  const alarmsRef = useRef<HTMLDivElement>(null)
  const doorMenuPlacement = useMenuPlacement(scrollViewRef, doorsRef)
  const cameraMenuPlacement = useMenuPlacement(scrollViewRef, camerasRef)
  const alarmMenuPlacement = useMenuPlacement(scrollViewRef, alarmsRef)
  const [aiOperatorCriteria, setAiOperatorCriteria] = useState<Criterion[]>([])

  const form = useForm<FormInputsT>({
    mode: 'onChange',
  })
  const {
    register,
    handleSubmit,
    control,
    formState,
    watch,
    trigger,
    getValues,
    reset,
  } = form
  const { isValid } = formState
  const watchFacility = watch('facility')
  const watchFloor = watch('floor')
  const watchCameras = watch('cameras')
  const watchDoors = watch('doors')
  const watchAlarms = watch('alarms')
  const watchEvents = watch('event')
  const watchRepeatType = watch('repeatType')
  const watchAction = watch('action')
  const watchEmails = watch('emails')

  const isAiOperatorActionSelected =
    watchAction?.value === Action.EnableAiOperator

  const {
    eventOptions,
    shouldIncludeAllFloors,
    shouldIncludeAllCameras,
    shouldIncludeAllDoors,
    shouldIncludeAllAlarms,
    shouldIncludeAllEvents,
    selectedStartDate,
    selectedEndDate,
    selectedEndTime,
    shouldShowDays,
    shouldRepeat,
    shouldEnd,
    days,
    timeZone,
    setShouldIncludeAllCameras,
    setShouldIncludeAllDoors,
    setShouldIncludeAllAlarms,
    setSelectedStartDate,
    setSelectedEndDate,
    updateEndTime,
    updateDate,
    setFacilityName,
    setShouldIncludeAllEvents,
    setShouldRepeat,
    setShouldShowDays,
    setShouldEnd,
    facilityOptions,
    floorOptions,
    cameraOptions,
    doorOptions,
    alarmOptions,
    emailOptions,
    criteriaOptions,
    isLoading,
    isFloorsLoading,
    isCamerasLoading,
    isDoorsLoading,
    isAlarmsLoading,
    isAiOperatorFieldsLoading,
    updateSelectedFacilityId,
    updateToggleAllFloors,
    updateSelectedFloorIds,
    updateNumberOfRepeats,
    updateSelectedDays,
    createRule,
    isActionOptionDisabled,
    isAllDay,
    toggleAllDaySwitch,
    isDryRun,
    onDryRunToggle,
  } = useCreateIncidentRules({
    form,
    criteria: aiOperatorCriteria,
    isActionAiOperator: isAiOperatorActionSelected,
  })

  const onSubmit: SubmitHandler<FormInputsT> = async (values) => {
    createRule(values)
  }

  const areDoorsSelected =
    (shouldIncludeAllDoors && doorOptions.length > 0) || watchDoors?.length > 0
  const areCamerasSelected =
    (shouldIncludeAllCameras && cameraOptions.length > 0) ||
    watchCameras?.length > 0
  const areAlarmsSelected =
    (shouldIncludeAllAlarms && alarmOptions.length > 0) ||
    watchAlarms?.length > 0
  const areEventsSelected = shouldIncludeAllEvents || watchEvents?.length > 0
  const isEndDateEnabledAndSelected = shouldEnd && !selectedEndDate

  // Check if ALARM event is selected
  const isAlarmEventSelected = watchEvents?.some(
    (ev) => ev.value === RuleEventType.Alarm
  )

  const isAiOperatorCriteriaSelected =
    isAiOperatorActionSelected && aiOperatorCriteria.length === 0

  const doorIds = useMemo(
    () =>
      shouldIncludeAllDoors
        ? getOptionValues(doorOptions)
        : getOptionValues(watchDoors),
    [shouldIncludeAllDoors, watchDoors, doorOptions]
  )
  // A camera is selected and a Line Crossing event is selected with a Mute action
  const isLineCrossingEventSelected = watchEvents?.some(
    (ev) => ev.value === RuleEventType.LineCrossing
  )
  const isValidLineCrossing =
    areCamerasSelected &&
    watchAction?.value === Action.Mute &&
    (shouldIncludeAllEvents || isLineCrossingEventSelected)

  // A door is selected and with valid door events, with a Mute action
  const areDoorEventsSelected = watchEvents?.some(
    (ev) =>
      ev.value === RuleEventType.DoorHeldOpen ||
      ev.value === RuleEventType.DoorForcedOpen ||
      ev.value === RuleEventType.DoorOpenAlarm
  )

  const isValidDoorEvent =
    areDoorsSelected &&
    (shouldIncludeAllEvents ||
      areValidEventsForDoorDevice(watchEvents, watchAction?.value))

  // Validate alarm event: alarm devices selected when ALARM event is selected
  const isValidAlarmEvent =
    areAlarmsSelected && (shouldIncludeAllEvents || isAlarmEventSelected)

  const isValidEvent =
    isValidDoorEvent || isValidLineCrossing || isValidAlarmEvent
  const isCreateDisabled =
    !isValid ||
    !isValidEvent ||
    !areEventsSelected ||
    !isEmailActionValid(watchAction?.value, watchEmails) ||
    (!isAllDay && !selectedEndTime) ||
    isEndDateEnabledAndSelected ||
    isAiOperatorCriteriaSelected

  const shouldShowCrossDayAlert =
    !isAllDay && selectedStartDate && selectedEndTime

  useEffect(() => {
    updateSelectedFacilityId(watchFacility)
  }, [watchFacility])

  useEffect(() => {
    updateSelectedFloorIds(watchFloor)
  }, [watchFloor])

  useEffect(() => {
    if (watchRepeatType) {
      const shouldShowDays = watchRepeatType.value === RepeatInterval.Weekly
      setShouldShowDays(shouldShowDays)
    }
  }, [watchRepeatType])

  useEffect(() => {
    // Clear values if AiOperator or Mute action is selected
    const watchActionValue = watchAction?.value
    if (
      watchActionValue === Action.EnableAiOperator ||
      watchActionValue === Action.Mute
    ) {
      reset({
        ...getValues(),
        event: null,
      })
    }
    trigger('event')
  }, [watchAction])

  // auto-populate all criteria when AI Operator action is selected
  useEffect(() => {
    if (aiOperatorCriteria.length === 0 && criteriaOptions.length > 0) {
      setAiOperatorCriteria(
        criteriaOptions.map((criterion) => ({
          id: uuid(),
          action: criterion.action,
          criterion: criterion.criterion,
          detected: criterion.detected,
          aiOperatorModelId: undefined,
        }))
      )
    }
  }, [criteriaOptions])

  return (
    <Flex direction='column' h='calc(100vh - 85px)'>
      <Flex
        direction='row'
        gap={{ base: '2px', md: '2px', lg: '6px' }}
        h='100%'
        overflowY='scroll'
        py='20px'
        ref={scrollViewRef}
        w='full'
      >
        {/* Column 1: Name, Facilities, Floors, Devices */}
        <Flex
          direction='column'
          flex={{ base: '1', lg: '0.9' }}
          px={{ base: '16px', md: '24px' }}
        >
          <RuleSection mb={6}>
            <FormTextareaControl
              id='name'
              label='Name of rule'
              labelColour='#1A202C'
              labelFontWeight='600'
              labelOpacity={1}
              maxCharCount={MAX_RULENAME_LENGTH}
              placeholder='Enter a name for this rule'
              textareaRef={register({
                maxLength: {
                  value: MAX_RULENAME_LENGTH,
                  message: `Rule name should have less than ${MAX_RULENAME_LENGTH} characters`,
                },
                required: 'Rule name is required',
              })}
            />
          </RuleSection>

          <RuleSection label='Select the devices associated with the rule'>
            <Box mb={6}>
              <FormInputSelectControl
                control={control}
                defaultValue={null}
                id='facility'
                isClearable
                isDisabled={false}
                isLoading={isLoading}
                isSearchable
                label='Facility'
                labelColour='#1A202C'
                labelFontWeight='600'
                labelOpacity={1}
                noOptionsMessage={({ inputValue }) =>
                  getFacilityNoOptionsMessage(inputValue)
                }
                onInputChange={setFacilityName}
                options={facilityOptions}
                rules={{ required: true }}
              />
            </Box>
            <Box mb={6}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='floor'
                isLoading={isFloorsLoading}
                label='Floor(s)'
                leftRadioButtonLabel='All floors'
                options={floorOptions}
                rightRadioButtonLabel='Select one or more floors'
                setToggleAll={(isToggled) =>
                  updateToggleAllFloors(isToggled, watchFloor)
                }
                toggleAll={shouldIncludeAllFloors}
              />
            </Box>
            <Box ref={doorsRef}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='doors'
                isLoading={isDoorsLoading}
                label='Door Device(s)'
                leftRadioButtonLabel='All Doors'
                menuPlacement={doorMenuPlacement}
                options={doorOptions}
                rightRadioButtonLabel='Select one or more doors'
                setToggleAll={setShouldIncludeAllDoors}
                toggleAll={shouldIncludeAllDoors}
              />
            </Box>
            <Box mt={3} ref={camerasRef}>
              <FormRadioInputControl
                control={control}
                dropdownContainerHeight='200px'
                id='cameras'
                isLoading={isCamerasLoading}
                label='Camera Device(s)'
                leftRadioButtonLabel='All Cameras'
                menuPlacement={cameraMenuPlacement}
                options={cameraOptions}
                rightRadioButtonLabel='Select one or more camera'
                setToggleAll={setShouldIncludeAllCameras}
                toggleAll={shouldIncludeAllCameras}
              />
            </Box>
            {(shouldIncludeAllEvents || isAlarmEventSelected) && (
              <Box mt={3} ref={alarmsRef}>
                <FormRadioInputControl
                  control={control}
                  dropdownContainerHeight='200px'
                  id='alarms'
                  isLoading={isAlarmsLoading}
                  label='Alarm Device(s)'
                  leftRadioButtonLabel='All Alarms'
                  menuPlacement={alarmMenuPlacement}
                  options={alarmOptions}
                  rightRadioButtonLabel='Select one or more alarm'
                  setToggleAll={setShouldIncludeAllAlarms}
                  toggleAll={shouldIncludeAllAlarms}
                />
              </Box>
            )}
          </RuleSection>
        </Flex>

        {/* Column 2: Event, Actions Schedule */}
        <Flex
          direction='column'
          flex={{ base: '1', lg: '1.1' }}
          px={{ base: '16px', md: '24px' }}
        >
          <RuleSection label='Rule Actions' mb={6}>
            <Box mb={6}>
              <FormInputSelectControl
                control={control}
                defaultValue={null}
                id='action'
                isClearable
                isDisabled={false}
                isLoading={isLoading}
                isOptionDisabled={isActionOptionDisabled}
                isSearchable
                label='Choose Action'
                labelColour='#1A202C'
                labelFontWeight='600'
                labelOpacity={1}
                options={actionOptions}
                rules={{ required: true }}
              />
            </Box>

            <EmailInputSelectControl
              control={control}
              id='emails'
              isLoading={isLoading}
              isOpen={watchAction?.value == Action.SendEmail}
              label='Recipients'
              options={emailOptions}
              placeholder='Add Recipients'
            />
            {isAiOperatorActionSelected && (
              <ScaleFade
                in={true}
                initialScale={0.95}
                transition={{ enter: { duration: 0.3 } }}
              >
                <AiOperatorCriteria
                  criteria={aiOperatorCriteria}
                  criteriaOptions={criteriaOptions}
                  doorIds={doorIds}
                  isLoading={isAiOperatorFieldsLoading}
                  onChange={setAiOperatorCriteria}
                />
                <DryRunToggle
                  isDryRun={isDryRun}
                  onDryRunToggle={onDryRunToggle}
                />
              </ScaleFade>
            )}

            <FormRadioInputControl
              control={control}
              id='event'
              label='Event(s)'
              leftRadioButtonLabel='All events'
              options={eventOptions}
              rightRadioButtonLabel='Select one or more events'
              setToggleAll={setShouldIncludeAllEvents}
              toggleAll={shouldIncludeAllEvents}
            />
            <EventSectionAlert
              shouldShow={!isValidEvent && isLineCrossingEventSelected}
              title='At least one camera device must be selected when creating a Line Crossing event'
            />
            <EventSectionAlert
              shouldShow={!isValidDoorEvent && areDoorEventsSelected}
              title='At least one door device must be selected'
            />
            <EventSectionAlert
              shouldShow={!isValidAlarmEvent && isAlarmEventSelected}
              title='At least one alarm device must be selected when creating an Alarm event'
            />
          </RuleSection>

          <RuleSection label='Select time frame for the action to be applied'>
            <RuleScheduleAlert
              facilityName={watchFacility?.label}
              timeZone={timeZone}
            />

            {shouldShowCrossDayAlert && (
              <CrossDayAlert
                endTime={selectedEndTime}
                startTime={selectedStartDate}
              />
            )}

            <Box mb={6}>
              <LabeledSwitch
                id='all-day-switch'
                inputRef={null}
                isChecked={isAllDay}
                label='All Day'
                onSwitchChange={toggleAllDaySwitch}
              />
              <FormDatePickerControl
                control={control}
                id='date'
                isAllDay={isAllDay}
                onChangeDate={updateDate}
                onChangeEndTime={updateEndTime}
                onChangeStartTime={setSelectedStartDate}
                selectedDate={selectedStartDate}
                selectedEndTime={selectedEndTime}
                showEndTimePicker
                showStartTimePicker
              />
            </Box>

            <RecurrenceToggle
              control={control}
              days={days}
              endDate={selectedEndDate}
              onEndDateChange={setSelectedEndDate}
              onRepeatValueChange={updateNumberOfRepeats}
              selectedStartDate={selectedStartDate}
              setDays={updateSelectedDays}
              setShouldEnd={setShouldEnd}
              setShouldRepeat={setShouldRepeat}
              shouldEnd={shouldEnd}
              shouldRepeat={shouldRepeat}
              shouldShowDays={shouldShowDays}
            />
          </RuleSection>
        </Flex>
      </Flex>

      <Flex justifyContent='flex-end' mb='40px' px='40px'>
        <Button
          isDisabled={isCreateDisabled}
          isLoading={isLoading}
          leftIcon={<HiOutlineCheckCircle size='24px' />}
          loadingText='Creating Rule'
          onClick={handleSubmit(onSubmit)}
          type='submit'
        >
          Create Rule
        </Button>
      </Flex>
    </Flex>
  )
}
